// src/slices/gamesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { graphqlRequest } from "../helpers"
import { normalizeGames } from "../helpers/gamesHelper"

const {
  REACT_APP_MARKET_UID: marketUid,
  REACT_APP_DOMAIN_ZONE: domainZone,
  REACT_APP_CODE: siteCode,
  REACT_APP_MEDIA_URL: mediaUrl,
  REACT_APP_IMAGEKIT_URL: imageKitUrl
} = process.env

// 🔎 SEARCH THUNK
export const fetchGamesBySearch = createAsyncThunk("games/fetchGamesBySearch", async searchText => {
  if (!searchText.trim()) return []

  const query = `
      query SearchGames($search: String!) {
        viewer {
          content {
            items(
              operatorUid: "${siteCode}"
              language: "${domainZone}"
              marketUid: "${marketUid}"
              filter: {
                type: { name: ["game"] },
                term: $search
              }
            ) {
              edges {
                node {
                  name
                  attributes {
                    slug
                    vertical
                    images {
                      tile { url }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `

  const searchWildcard = `${searchText.trim()}*`
  const response = await graphqlRequest(query, { search: searchWildcard })
  const edges = response?.viewer?.content?.items?.edges || []
  return normalizeGames(edges, mediaUrl, imageKitUrl)
})

// 🟦 CATEGORY THUNK
export const fetchGamesByCategory = createAsyncThunk("games/fetchGamesByCategory", async categorySlug => {
  const query = `
      query GamesByCategory($category: String!) {
        viewer {
          content {
            items(
              operatorUid: "${siteCode}"
              language: "${domainZone}"
              marketUid: "${marketUid}"
              filter: {  attributes: {category: [$category]}}
              first: 100
            ) {
              edges {
                node {
                  name
                  attributes {
                    slug
                    vertical
                    images {
                      tile { url }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
  const data = await graphqlRequest(query, { category: categorySlug })
  const edges = data?.viewer?.content?.items?.edges || []
  return normalizeGames(edges, mediaUrl, imageKitUrl)
})

// 🧱 STATE
const initialState = {
  items: [], // full game list (initial)
  searchItems: [], // search results
  categoryItems: [], // category results
  categories: [],
  filter: "",
  loading: false,
  error: null
}

const gamesSlice = createSlice({
  name: "games",
  initialState,
  reducers: {
    setGames(state, action) {
      state.items = action.payload || []
    },
    setCategories(state, action) {
      state.categories = action.payload || []
    },
    setFilter(state, action) {
      state.filter = action.payload
    },
    clearGames(state) {
      state.items = []
      state.searchItems = []
      state.categoryItems = []
      state.categories = []
      state.filter = ""
    }
  },
  extraReducers: builder => {
    builder
      // SEARCH
      .addCase(fetchGamesBySearch.pending, state => {
        state.loading = true
      })
      .addCase(fetchGamesBySearch.fulfilled, (state, action) => {
        state.loading = false
        state.searchItems = action.payload
      })
      .addCase(fetchGamesBySearch.rejected, (state, action) => {
        state.loading = false
        state.searchItems = []
        state.error = action.error.message
      })

      // CATEGORY
      .addCase(fetchGamesByCategory.pending, state => {
        state.loading = true
      })
      .addCase(fetchGamesByCategory.fulfilled, (state, action) => {
        state.loading = false
        state.categoryItems = action.payload
      })
      .addCase(fetchGamesByCategory.rejected, (state, action) => {
        state.loading = false
        state.categoryItems = []
        state.error = action.error.message
      })
  }
})

// 🔍 SELECTORS
export const selectGamesCategories = state => state.games.categories
export const selectGamesFilter = state => state.games.filter

export const selectFilteredGames = (state, categorySlug = null) => {
  const { items, searchItems, categoryItems, filter } = state.games

  if (filter) return searchItems
  if (categorySlug) return categoryItems
  return items
}

export const { setGames, setCategories, setFilter, clearGames } = gamesSlice.actions

export default gamesSlice.reducer
